/**
 * Workflow builder helpers: validation checklist, step search, sticky notes.
 */

import { resolveTriggerFromLegacy } from './workflowTriggerCatalog';

export const STICKY_NOTE_COLORS = [
  { id: 'yellow', className: 'bg-yellow-200 border-yellow-300' },
  { id: 'green', className: 'bg-green-200 border-green-300' },
  { id: 'orange', className: 'bg-orange-200 border-orange-300' },
  { id: 'cyan', className: 'bg-cyan-200 border-cyan-300' },
  { id: 'gray', className: 'bg-slate-200 border-slate-300' },
  { id: 'mint', className: 'bg-emerald-200 border-emerald-300' },
  { id: 'purple', className: 'bg-purple-200 border-purple-300' },
  { id: 'pink', className: 'bg-pink-200 border-pink-300' },
];

export function createStickyNote(color = 'yellow', x = 120, y = 120) {
  const now = new Date().toISOString();
  return {
    id: `note_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    color,
    x,
    y,
    text: '',
    created_at: now,
    updated_at: now,
  };
}

export function getStepDisplayLabel(step) {
  if (step?.type === 'trigger') {
    const resolved = resolveTriggerFromLegacy(step);
    if (resolved?.name) return resolved.name;
  }
  return step?.label || step?.name || step?.config?.actionName || step?.config?.subject || step?.type || 'Action';
}

export function searchWorkflowSteps(steps, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];

  return (steps || []).flatMap((step, index) => {
    const label = getStepDisplayLabel(step);
    const haystack = [
      label,
      step.type,
      step.config?.subject,
      step.config?.message,
      step.config?.body,
      step.config?.action_key,
      step.config?.body_preview,
      step.notes,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (!haystack.includes(q)) return [];
    return [{
      stepId: step.id,
      index,
      type: step.type,
      label,
      matchText: label,
    }];
  });
}

export function runWorkflowChecklist(workflow) {
  const steps = workflow?.steps || [];
  const checks = [];

  checks.push({
    id: 'trigger',
    label: 'Workflow has at least one trigger',
    status: workflow?.trigger?.id || workflow?.trigger_event ? 'pass' : 'error',
    message: workflow?.trigger?.name || workflow?.trigger_event || 'No trigger selected',
  });

  steps.forEach((step) => {
    if (step.type === 'send_email') {
      const subject = step.config?.subject?.trim();
      const body = step.config?.body || step.config?.body_html || step.config?.html;
      const hasBody = Boolean(body && String(body).replace(/<[^>]*>/g, '').trim());
      checks.push({
        id: `email-subject-${step.id}`,
        label: `Email "${getStepDisplayLabel(step)}" has subject`,
        status: subject ? 'pass' : 'error',
      });
      checks.push({
        id: `email-body-${step.id}`,
        label: `Email "${getStepDisplayLabel(step)}" has body`,
        status: hasBody ? 'pass' : 'error',
      });
    }
    if (step.type === 'send_sms') {
      const message = step.config?.message?.trim();
      checks.push({
        id: `sms-body-${step.id}`,
        label: `SMS "${getStepDisplayLabel(step)}" has body`,
        status: message ? 'pass' : 'warning',
      });
    }
    if (step.type === 'backend_action' || step.type === 'action' || step.config?.requires_backend) {
      checks.push({
        id: `backend-key-${step.id}`,
        label: `Backend action "${getStepDisplayLabel(step)}" has action key`,
        status: step.config?.action_key ? 'pass' : 'error',
      });
    }
  });

  const hasErrors = checks.some((c) => c.status === 'error');
  const hasWarnings = checks.some((c) => c.status === 'warning');
  checks.push({
    id: 'publishable',
    label: 'Workflow can be published',
    status: hasErrors ? 'error' : hasWarnings ? 'warning' : 'pass',
    message: hasErrors ? 'Fix errors before publishing' : 'Ready to publish',
  });

  return checks;
}

export function isTypingTarget(target) {
  if (!target) return false;
  const tag = target.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  return Boolean(target.isContentEditable);
}

export function getWorkflowStepDescription(step, stepConfig = {}) {
  const config = step?.config || {};

  switch (step?.type) {
    case 'wait':
      if (config.duration) {
        const plural = config.duration > 1 ? 's' : '';
        return `Wait ${config.duration} ${config.unit || 'hour'}${plural}`;
      }
      return 'Configure wait time...';
    case 'send_email':
    case 'email_action':
      return config.subject || config.body_preview || config.body?.substring?.(0, 60) || 'Configure email...';
    case 'send_sms':
      return config.message || config.body_preview || 'Configure SMS...';
    case 'backend_action':
    case 'action':
      if (config.action_key) return `Action: ${config.action_key}`;
      return step?.description || 'Backend action';
    case 'staff_alert':
      return config.message
        ? `${config.message.substring(0, 60)}${config.message.length > 60 ? '...' : ''}`
        : 'Configure staff alert...';
    case 'audit_log':
      return config.events?.length ? `${config.events.length} audit event(s)` : config.note || 'Audit logging';
    case 'security_lock':
      return config.lock_minutes ? `Lock ${config.lock_minutes} min` : 'Security lockout';
    case 'update_record':
      return config.record && config.field ? `${config.record}.${config.field}` : 'Update record';
    case 'branch':
      return config.branch_key ? `Branch: ${config.branch_key}` : 'Branch path';
    case 'trigger': {
      const resolved = resolveTriggerFromLegacy(step);
      if (resolved?.description) return resolved.description;
      return step?.description || config.description || 'Workflow trigger';
    }
    case 'delay':
      if (config.duration) {
        const plural = config.duration > 1 ? 's' : '';
        return `Wait ${config.duration} ${config.unit || 'hour'}${plural}`;
      }
      return config.wait_for || 'Configure wait...';
    case 'apply_tag':
    case 'add_tag':
      if (config.tagNames?.length) return `Tags: ${config.tagNames.join(', ')}`;
      return 'Select tags to apply...';
    case 'remove_tag':
      if (config.tagNames?.length) return `Remove: ${config.tagNames.join(', ')}`;
      return 'Select tags to remove...';
    case 'grant_portal_access':
      if (config.accessLevel) {
        const levelLabel = config.accessLevel.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
        return `Level: ${levelLabel}`;
      }
      return 'Configure access level...';
    case 'revoke_portal_access':
      return config.configured ? 'Revoke access' : 'Configure...';
    case 'grant_offer_access':
      return config.offerName || 'Select offer/product...';
    case 'revoke_offer_access':
      return config.offerName ? `Revoke: ${config.offerName}` : 'Select offer to revoke...';
    case 'condition':
    case 'if_else':
      if (config.branches?.length) {
        const first = config.branches[0];
        const summary = first?.conditions?.find((c) => c.fieldLabel || c.field);
        if (summary) {
          const field = summary.fieldLabel || summary.field;
          const op = (summary.operator || '').replace(/_/g, ' ');
          const val = summary.valueLabel || (Array.isArray(summary.value) ? summary.value.join(', ') : summary.value);
          return val ? `${field} ${op} ${val}` : `${field} ${op}`.trim();
        }
        return `${config.branches.length} branch(es)`;
      }
      if (config.conditions?.length) {
        return `${config.conditions.length} condition(s) - ${config.logic?.toUpperCase() || 'AND'}`;
      }
      return 'Configure conditions...';
    case 'internal_notification':
    case 'send_notification':
      return config.message ? `${config.message.substring(0, 40)}...` : 'Configure notification...';
    case 'create_task':
      return config.title || 'New Task';
    default:
      return config.actionName || stepConfig.label || 'Configure action...';
  }
}
